/**
 * LovedOnePicker — chip row that auto-fills patient details from a saved
 * family member. Fetches /me/loved-ones once and lets the user tap a chip
 * (or a self-me pill) to hydrate the parent form's patient_name / age /
 * gender / phone fields.
 *
 * Props:
 *   onSelect(profile)  — receives { name, age, gender, phone, loved_one_id, kind }
 *                         'self' passes a profile whose loved_one_id is null.
 *   selectedId         — id of the currently-active loved-one (null = self)
 *   showSelf           — include a "Me" chip that yields the customer's own
 *                         profile (default true)
 *   testidPrefix       — DOM test-id namespace (default "lop")
 */
import React, { useEffect, useState } from "react";
import { custApi } from "../lib/customerApi";
import { UserCircle, Users } from "@phosphor-icons/react";

export default function LovedOnePicker({
  onSelect, selectedId, showSelf = true, testidPrefix = "lop",
}) {
  const [self, setSelf] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    Promise.all([
      custApi.get("/me/profile").catch(() => null),
      custApi.get("/me/loved-ones").catch(() => ({ data: { loved_ones: [] } })),
    ]).then(([p, l]) => {
      if (p?.data) {
        const d = p.data;
        setSelf({
          name: d.name || "",
          age: d.age || d.dob ? computeAge(d.dob) : "",
          gender: d.gender || "",
          phone: d.phone || "",
        });
      }
      setItems(l?.data?.loved_ones || []);
    });
  }, []);

  const pickSelf = () => {
    if (!self) return;
    onSelect({
      name: self.name,
      age: self.age,
      gender: self.gender,
      phone: self.phone,
      loved_one_id: null,
      kind: "self",
    });
  };

  const pickOne = (lo) => {
    onSelect({
      name: lo.name,
      age: lo.age || "",
      gender: lo.gender || "",
      phone: lo.phone || "",
      loved_one_id: lo.id,
      kind: lo.kind || "family",
    });
  };

  if (!self && items.length === 0) return null;

  return (
    <div className="sm:col-span-2" data-testid={`${testidPrefix}-picker`}>
      <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780] flex items-center gap-1">
        <Users size={11}/> Book for
      </label>
      <div className="mt-1 flex flex-wrap gap-2">
        {showSelf && self && (
          <button type="button" onClick={pickSelf}
                  data-testid={`${testidPrefix}-self`}
                  className={`h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${
                    selectedId == null
                      ? "bg-[#DC2626] text-white border-[#DC2626]"
                      : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                  }`}>
            <UserCircle size={13} weight="fill"/> Me{self.name ? ` · ${self.name.split(" ")[0]}` : ""}
          </button>
        )}
        {items.map(lo => {
          const on = selectedId === lo.id;
          return (
            <button key={lo.id} type="button" onClick={() => pickOne(lo)}
                    data-testid={`${testidPrefix}-lo-${lo.id}`}
                    className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                      on
                        ? "bg-[#DC2626] text-white border-[#DC2626]"
                        : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              {lo.name}
              {(lo.kind || lo.age) && (
                <span className={`ml-1 text-[10px] font-normal ${on ? "text-white/80" : "text-[#8A8780]"}`}>
                  · {[lo.kind, lo.age && `${lo.age}y`].filter(Boolean).join(" ")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function computeAge(dobIso) {
  if (!dobIso) return "";
  try {
    const dob = new Date(dobIso);
    const diff = Date.now() - dob.getTime();
    return Math.floor(diff / (365.25 * 86400 * 1000));
  } catch { return ""; }
}
